<app-app-layout>
  <div class="review-container">
    <!-- Progress Bar -->
    <app-simple-stepper [currentStep]="3" [totalSteps]="3"></app-simple-stepper>

    <div class="review-content">
      <!-- Plan Summary -->
      <div class="summary-card">
        <div class="card-header">
          <h3>Plan Summary</h3>
          <button mat-button color="primary" (click)="goBackToPlans()">
            <mat-icon>edit</mat-icon>
            Change Plan
          </button>
        </div>

        <div class="plan-details">
          <div class="plan-info">
            <h4>{{ selectedPlan?.planLabel }}</h4>
            <p>
              {{ selectedPlan?.name }}
              Subscription
            </p>
          </div>

          <!-- Selected Branch Display -->
          <div class="selected-branch-info" *ngIf="selectedBranchName">
            <mat-icon class="branch-icon">fitness_center</mat-icon>
            <div class="branch-details">
              <span class="branch-label">Selected Branch:</span>
              <span class="branch-name">{{ selectedBranchName }}</span>
            </div>
          </div>

          <!-- Branch Missing Error -->
          <div
            class="branch-error-info"
            *ngIf="isBranchNameMissing || !selectedBranchName"
          >
            <mat-icon class="error-icon">warning</mat-icon>
            <div class="error-details">
              <span class="error-message"
                >Branch selection is missing or invalid</span
              >
              <span class="error-action"
                >Please go back to the plan page and select a branch. It's
                mandatory to proceed.</span
              >
            </div>
            <button
              mat-raised-button
              color="primary"
              (click)="goBackToPlans()"
              class="go-back-button"
            >
              <mat-icon>arrow_back</mat-icon>
              Go Back to Select Branch
            </button>
          </div>

          <div class="plan-features">
            <div
              class="feature-item"
              *ngFor="let feature of selectedPlan?.featureList"
            >
              <mat-icon class="feature-icon">check_circle</mat-icon>
              <span>{{ feature }}</span>
            </div>
          </div>

          <div class="usage-limits">
            <div class="usage-item">
              <mat-icon>group</mat-icon>
              <span>{{ selectedPlan?.usageLimit?.members?.label }}</span>
            </div>
            <div class="usage-item">
              <mat-icon>person</mat-icon>
              <span>{{ selectedPlan?.usageLimit?.staff?.label }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Billing Information -->
      <div class="summary-card">
        <div class="card-header">
          <h3>Billing Information</h3>
          <button mat-button color="primary" (click)="goBackToBilling()">
            <mat-icon>edit</mat-icon>
            Edit Details
          </button>
        </div>

        <div class="billing-details">
          <div class="address-info">
            <div class="address-line">{{ billingInfo?.addressLine }}</div>
            <div class="location-details" *ngIf="!isInternationalPayment">
              {{ billingInfo?.district }}, {{ billingInfo?.state }} -
              {{ billingInfo?.pinCode }}
            </div>
            <div class="country">{{ billingInfo?.country }}</div>
          </div>

          <div class="gst-info" *ngIf="!isInternationalPayment">
            <div class="gst-status">
              <mat-icon>{{
                billingInfo?.hasGST ? "verified" : "info"
              }}</mat-icon>
              <span>
                {{
                  billingInfo?.hasGST ? "GST Registered" : "Not GST Registered"
                }}
              </span>
            </div>
            <div class="gst-number" *ngIf="billingInfo?.gstNumber">
              GST: {{ billingInfo?.gstNumber }}
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Summary -->
      <div class="summary-card payment-summary">
        <div class="card-header">
          <h3>Payment Summary</h3>
        </div>

        <div class="payment-breakdown">
          <div class="amount-row">
            <span class="label">Plan Amount</span>
            <span class="amount price-inline" *ngIf="hasDiscount(); else plainAmount">
              <span class="original-amount">{{
                formatCurrency(selectedPlan?.originalAmount)
              }}</span>
              <span class="payable-amount">{{
                formatCurrency(selectedPlan?.amount)
              }}</span>
            </span>
            <ng-template #plainAmount>
              <span class="amount">{{
                formatCurrency(selectedPlan?.amount)
              }}</span>
            </ng-template>
          </div>

          <!-- GST Line (India only) -->
          <div class="amount-row gst-row" *ngIf="!isInternationalPayment">
            <span class="label">
              +{{ getGstPercentage() }}% GST
              <span class="gst-note" [class.claimable]="isGstClaimable()">
                ({{ isGstClaimable() ? "Claimable ITC" : "Non-claimable" }})
              </span>
            </span>
            <span class="amount">{{
              formatCurrency(calculateGstAmount())
            }}</span>
          </div>

          <!-- GST Breakdown (India only) -->
          <div class="gst-breakdown-section" *ngIf="!isInternationalPayment">
            <div class="breakdown-header">
              <h4>GST Breakdown</h4>
              <div class="user-state-info">
                <span
                  class="gst-type"
                  [class.same-state]="isSameState()"
                  [class.different-state]="!isSameState()"
                >
                  {{ getGstType() }}
                </span>
              </div>
            </div>

            <div class="breakdown-items">
              <div
                *ngFor="let item of getGstBreakdown()"
                class="breakdown-item"
              >
                <div class="gst-type-info">
                  <span class="gst-type-name">{{ item.type }}</span>
                  <span class="gst-percentage">{{ item.percentage }}%</span>
                </div>
                <div class="gst-amount">
                  ₹{{ item.amount | number : "1.2-2" }}
                </div>
              </div>
            </div>
          </div>

          <div class="amount-row total-row">
            <span class="label total-label">Total Amount</span>
            <span class="amount total-amount">{{
              formatCurrency(calculateTotalAmount())
            }}</span>
          </div>
        </div>

        <div class="payment-note">
          <mat-icon>security</mat-icon>
          <span>Payment securely processed by Razorpay</span>
        </div>

        <div class="qr-fallback" *ngIf="!isInternationalPayment">
          <span>UPI not working?</span>
          <button
            mat-button
            color="primary"
            class="qr-fallback-button"
            (click)="generateQRCode()"
            [disabled]="isBranchNameMissing || !selectedBranchName || isQrLoading"
          >
            <mat-icon>{{ isQrLoading ? "hourglass_empty" : "qr_code" }}</mat-icon>
            {{
              isQrLoading
                ? "Generating..."
                : qrCodeUrl
                ? "Regenerate UPI QR"
                : "Generate UPI QR"
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- UPI QR Code Section -->
    <div class="summary-card qr-card" *ngIf="!isInternationalPayment && qrCodeUrl">
      <div class="qr-content">
        <div class="qr-header">
          <h4>UPI QR Generated</h4>
          <p>Scan, download, or share this QR if checkout UPI is not working.</p>
        </div>
        <div class="qr-image-placeholder" *ngIf="isQrImageLoading">
          <mat-icon>qr_code</mat-icon>
          <span>Loading UPI QR...</span>
        </div>
        <img
          [src]="qrCodeUrl"
          alt="UPI QR Code"
          class="qr-image"
          [class.hidden]="isQrImageLoading"
          (load)="onQrImageLoad()"
          (error)="onQrImageError()"
        />
        <p class="qr-hint">
          Scan with any UPI app to pay
          {{ formatCurrency(calculateTotalAmount()) }}
        </p>
        <p class="qr-status" *ngIf="qrStatusMessage">
          <mat-icon *ngIf="isQrPolling">sync</mat-icon>
          {{ qrStatusMessage }}
        </p>
        <div class="qr-actions">
          <button mat-stroked-button color="primary" (click)="downloadQR()">
            <mat-icon>download</mat-icon>
            Download QR
          </button>
          <button mat-stroked-button color="primary" (click)="viewQR()">
            <mat-icon>open_in_new</mat-icon>
            Open QR
          </button>
        </div>
      </div>
    </div>

    <!-- Sticky Payment Button -->
    <div class="sticky-payment-bar">
      <button
        mat-raised-button
        color="primary"
        class="pay-now-button"
        (click)="payNow()"
        [disabled]="isBranchNameMissing || !selectedBranchName"
      >
        <mat-icon>payment</mat-icon>
        Pay Now {{ formatCurrency(calculateTotalAmount()) }}
      </button>
    </div>
  </div>
</app-app-layout>
